import axios, { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { AuthProvider, HttpError } from 'react-admin';
import { PermissionData } from '../entities/permission.entity';
import { UserModel } from '../entities/user.entity';
import * as authService from '../services/authService';
import { AuthModel } from '../services/authService';
import * as permissionService from '../services/permissionService';
import { StorageKey, XStorage } from './storage';
import { clearStepUp, isStepUpRefusal } from './stepUp';
import { getAuthWithRefresh } from './tokenRefresh';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

/** How long a user's permissions are trusted before being re-fetched. */
const PERMISSION_CACHE_TTL_MS = 60_000;

export async function getAuth() {
  return getAuthWithRefresh();
}

async function login(params: any) {
  // The sign-in view completes the two-factor step itself and hands over the token pair (XBIT-4742).
  if (params?.tokens) {
    const tokens = params.tokens as AuthModel;
    await XStorage.set(StorageKey.AuthData, tokens);
    return tokens;
  }

  const { username, password } = params;

  const auth = await authService.login({ username, password }).catch((err) => {
    if (err instanceof AxiosError) {
      throw err.response?.data;
    }
    throw err;
  });

  // A challenge body is a credential for /auth/2fa/* only, never a session: it is refused here, not stored.
  // The sign-in view runs the two-factor step itself and hands over the token pair as `tokens`.
  if (!('accessToken' in auth)) {
    throw new Error('Two-factor verification is required to sign in');
  }

  await XStorage.set(StorageKey.AuthData, auth);
  return auth;
}

const authProvider: AuthProvider = {
  login,
  async logout() {
    // XBIT-4777: the step-up grant belongs to the session being ended.
    clearStepUp();
    // XBIT-4826 D10.3: end the server-side session first. The token comes through getAuth() so one that is about
    // to expire is refreshed, and the call goes through axios so a refused token is refreshed and retried. The
    // outcome never blocks the sign-out; with nothing stored (another tab already signed out) no call is made.
    const auth = await getAuth().catch(() => null);
    if (auth?.accessToken) {
      await authService.logoutSession(auth.accessToken).catch(() => undefined);
    }
    await Promise.all([
      XStorage.remove(StorageKey.AuthData),
      XStorage.remove(StorageKey.UserData),
      XStorage.remove(StorageKey.Permission),
      localStorage.removeItem(`${StorageKey.Permission}_time`),
    ]);
  },
  async getPermissions() {
    const auth = await getAuth();
    if (!auth?.accessToken) return null;

    // Cached with a short TTL rather than indefinitely: when an admin edits a
    // role the server rejects the user immediately, but the UI keeps showing the
    // old menu until it re-reads permissions. `checkError` only clears the cache
    // for requests that go through react-admin's dataProvider, and several
    // screens call axios directly, so without an expiry those users would keep
    // the stale menu until they log out.
    const permission = await XStorage.getWithTTL<PermissionData>(
      StorageKey.Permission,
      PERMISSION_CACHE_TTL_MS
    );
    if (permission) return permission;

    const response = await permissionService.getAll();
    const result = response?.data as PermissionData;

    await XStorage.setWithTTL(StorageKey.Permission, result);
    return result;
  },
  async checkAuth(params: any) {
    const auth = await getAuth();
    if (!auth) {
      throw new Error('Session expired!');
    }
    const decoded = jwtDecode(auth?.accessToken);

    const expired = new Date((decoded.exp || 0) * 1000);

    if (expired.getTime() < Date.now()) {
      throw new Error('Session expired!');
    }
  },

  async invalidatePermissionsCache() {
    await XStorage.remove(StorageKey.Permission);
    localStorage.removeItem(`${StorageKey.Permission}_time`);
  },

  async checkError(params: any) {
    const status =
      params instanceof AxiosError ? params.response?.status : params instanceof HttpError ? params.status : undefined;

    // XBIT-4777: a step-up refusal says nothing about the user's permissions. Clearing the cache would re-fetch
    // them, remount the guarded screen and send the refused request again, in a loop.
    if (status === 403 && !isStepUpRefusal(params)) {
      await XStorage.remove(StorageKey.Permission);
      localStorage.removeItem(`${StorageKey.Permission}_time`);
    }

    // XBIT-4826 D10.5: a 401 signs out only when nothing is stored any more. The interceptor already refreshed,
    // retried, or cleared the pair; a handler-level 401 (a wrong old password) must not end the session.
    if (status === 401) {
      const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
      if (!stored) throw params;
    }
    return Promise.resolve();
  },

  async getIdentity() {
    const user = await XStorage.get<UserModel>(StorageKey.UserData);
    if (user) return user;
    const { accessToken } = (await getAuth()) || {};
    const url = `${baseUrl}/auth/me`;
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    };
    const resp = await axios.get<UserModel>(url, {
      headers,
    });
    const data = resp?.data;

    await XStorage.set(StorageKey.UserData, data);
    return data;
  },
};

export default authProvider;
